import {useFlux} from '@nlabs/arkhamjs-utils-react';
import {restRequest} from '@nlabs/metropolisjs';
import type {ReactElement} from 'react';
import {useEffect, useRef} from 'react';

type SessionResponse = Readonly<{
  readonly authenticated?: boolean;
  readonly session?: Record<string, unknown> | null;
}>;

export const SESSION_IDLE_TIMEOUT_MS = 60 * 60 * 1000;
export const SESSION_REFRESH_INTERVAL_MS = 15 * 60 * 1000;
const SESSION_REFRESH_RETRY_MS = 60 * 1000;

export const isSessionRefreshDue = (lastActivity: number, lastRefresh: number, now: number): boolean => (
  now - lastActivity < SESSION_IDLE_TIMEOUT_MS && now - lastRefresh >= SESSION_REFRESH_INTERVAL_MS
);

export const SessionActivityRefresh = (): ReactElement | null => {
  const flux = useFlux();
  const lastActivity = useRef(Date.now());
  const lastRefresh = useRef(Date.now());
  const refreshing = useRef(false);

  useEffect(() => {
    const refreshSession = async (): Promise<void> => {
      if(refreshing.current || !flux.getState('user.session.userId', '')) return;
      refreshing.current = true;
      lastRefresh.current = Date.now();
      try {
        const response = await restRequest<SessionResponse>(flux, 'session');
        if(response?.authenticated && response.session?.userId) await flux.setState('user.session', response.session);
        else await flux.setState('user.session', {});
      } catch {
        // A transient refresh failure should not sign out an otherwise valid session.
        lastRefresh.current = Date.now() - SESSION_REFRESH_INTERVAL_MS + SESSION_REFRESH_RETRY_MS;
      } finally {
        refreshing.current = false;
      }
    };

    const recordActivity = (): void => {
      const now = Date.now();
      const shouldRefresh = isSessionRefreshDue(lastActivity.current, lastRefresh.current, now);
      lastActivity.current = now;
      if(shouldRefresh) void refreshSession();
    };

    const activityEvents = ['keydown', 'pointerdown', 'scroll', 'touchstart'] as const;
    activityEvents.forEach((eventName) => window.addEventListener(eventName, recordActivity, {passive: true}));
    return () => activityEvents.forEach((eventName) => window.removeEventListener(eventName, recordActivity));
  }, [flux]);

  return null;
};
